import {
    CoverPage,
    DocumentDrinkSizes,
    hasFormatEditor,
    isTitleItem,
    isWineItem,
    MenuDocument,
    MenuFormat,
    MenuItem,
    MenuPage,
    PrintMode,
    WineItem
} from "./MenuDocumentModel";
import {createAsyncThunk, createSlice, Draft, PayloadAction} from "@reduxjs/toolkit";
import {AppDispatch, AppState} from "lib/redux/store";
import {
    createTitle,
    EditorItem,
    getLastItemKey,
    isExistingItemKey, nextEditorItemKey,
    redistributeItemsToFitPages,
    refocusAfterItemRedistribution,
    toKeyedEditorItemList,
    updateDrinkSizes
} from "./editorUtils";
import {add, assoc, equals, evolve, isEmpty, last, map} from "ramda";
import { createDocumentHandler } from "./documentHandlerUtils";

export type EditorItemKey = { page: number, item: number }

interface EditorState {
    winePickerActive: boolean
    wineScannerPickerActive: boolean
    aperitifPickerActive: boolean
    formatOptionsActive: boolean
    coverPageEditorActive: boolean
    draggingItem: EditorItemKey | null
    focusedItem: EditorItemKey | null
    deletePopoverActive: boolean
    deletePopoverPage: number | null
    printPopoverActive: boolean
    confirmSavePopoverActive: boolean
    document: MenuDocument | null
}

const initialState: EditorState = {
    winePickerActive: false,
    wineScannerPickerActive: false,
    aperitifPickerActive: false,
    formatOptionsActive: false,
    coverPageEditorActive: false,
    draggingItem: null,
    focusedItem: null,
    deletePopoverActive: false,
    deletePopoverPage: null,
    printPopoverActive: false,
    confirmSavePopoverActive: false,
    document: null
}

interface AddAction {
    after?: EditorItemKey
    items: MenuItem[]
}

interface MoveAction {
    old: EditorItemKey
    new: EditorItemKey
}

interface MoveByOneAction {
    itemKey: EditorItemKey
    dir: number
}

interface ReplaceAction {
    key: EditorItemKey
    newItem: MenuItem
}

export interface AddWineCategoryAction {
    category: string
    wines: WineItem[]
}

const {actions, reducer} = createSlice({
    name: 'menuEditor',
    initialState: initialState,
    reducers: {
        setDocument: (state, action: PayloadAction<MenuDocument>) => {
            state.document = action.payload
        },

        resetDocumentState: state => {
            state.winePickerActive = false;
            state.wineScannerPickerActive = false;
            state.aperitifPickerActive = false;
            state.formatOptionsActive = false;
            state.coverPageEditorActive = false;
            state.draggingItem = null;
            state.focusedItem = null;
            state.deletePopoverActive = false;
            state.deletePopoverPage = null;
            state.printPopoverActive = false;
            state.confirmSavePopoverActive = false;
        },

        setWinePickerActive: (state, action: PayloadAction<boolean>) => {
            state.winePickerActive = action.payload;
        },

        setWineScannerPickerActive: (state, action: PayloadAction<boolean>) => {
            state.wineScannerPickerActive = action.payload;
        },

        setAperitifPickerActive: (state, action: PayloadAction<boolean>) => {
            state.aperitifPickerActive = action.payload;
        },

        setDraggingItem: (state, action: PayloadAction<EditorItemKey>) => {
            state.draggingItem = action.payload;
        },

        setFocusedItem: (state, action: PayloadAction<EditorItemKey | null>) => {
            if (action.payload == null) {
                state.focusedItem = null;
            } else {
                const pages: MenuPage[] = state.document?.pages ?? [];
                const {page, item} = action.payload;
                if (page in pages && item in pages[page]) {
                    state.focusedItem = action.payload;
                    state.formatOptionsActive = false;
                    state.coverPageEditorActive = false;
                }
            }
        },

        setFormatOptionsActive: (state, action: PayloadAction<boolean>) => {
            if (state.document && !hasFormatEditor(state.document.type)) {
                action.payload = false;
            }
            state.formatOptionsActive = action.payload;
            if (action.payload) {
                state.focusedItem = null;
                state.coverPageEditorActive = false;
            }
        },

        setCoverPageEditorActive: (state, action: PayloadAction<boolean>) => {
            if (state.document && !hasFormatEditor(state.document.type)) {
                action.payload = false;
            }
            state.coverPageEditorActive = action.payload;
            if (action.payload) {
                state.focusedItem = null;
                state.formatOptionsActive = false;
            }
        },

        setFormat: (state, action: PayloadAction<MenuFormat>) => {
            if (state.document == null || !hasFormatEditor(state.document.type)) {
                return;
            }
            
            state.document.format = action.payload;
            updateItemSizesDistributionAndFocus(state);
        },

        updateItemSizesDistributionAndFocus: (state) => {
            updateItemSizesDistributionAndFocus(state);
        },

        setGlobalSuitableFood: (state, action: PayloadAction<boolean>) => {
            if (state.document == null) {
                return;
            }

            state.document.pages = map(
                page => map(
                    item => isWineItem(item) ? assoc('showSuitableFood', action.payload, item) : item,
                    page
                ),
                state.document.pages
            );
        },

        setCoverPage: (state, action: PayloadAction<CoverPage | null>) => {
            if (state.document == null) {
                return;
            }

            state.document.coverPage = action.payload;
        },

        setCoverPageVariant: (state, action: PayloadAction<'1' | '2' | '3'>) => {
            if (state.document == null || state.document.coverPage == null) {
                return;
            }

            state.document.coverPage.variant = action.payload;
        },

        setCoverPageTitle: (state, action: PayloadAction<string>) => {
            if (state.document == null || state.document.coverPage == null) {
                return;
            }

            state.document.coverPage.title = action.payload;
        },

        setPrintMode: (state, action: PayloadAction<PrintMode>) => {
            if (state.document == null) {
                return;
            }

            state.document.printMode = action.payload;
        },

        setSizes: (state, action: PayloadAction<DocumentDrinkSizes>) => {
            if (state.document == null) {
                return;
            }

            state.document.sizes = action.payload;
            updateItemSizesDistributionAndFocus(state);
        },

        setIsDegu: (state, action: PayloadAction<boolean>) => {
            if (state.document == null) {
                return;
            }

            state.document.degu = action.payload;
        },

        setShowAppellation: (state, action: PayloadAction<boolean>) => {
            if (state.document == null) {
                return;
            }

            state.document.showAppellation = action.payload;
        },

        setShowYear: (state, action: PayloadAction<boolean>) => {
            if (state.document == null) {
                return;
            }

            state.document.showYear = action.payload;
        },

        setDisplayFooter: (state, action: PayloadAction<boolean>) => {
            if (state.document == null) {
                return;
            }

            state.document.displayFooter = action.payload;
        },

        openDeletePopover: (state, action: PayloadAction<number>) => {
            state.deletePopoverActive = true;
            state.deletePopoverPage = action.payload;
        },

        closeDeletePopover: (state) => {
            state.deletePopoverActive = false;
            state.deletePopoverPage = null;
        },

        deletePage: (state, action: PayloadAction<number>) => {
            if (state.document == null) {
                return;
            }

            state.document.pages.splice(action.payload, 1);
        },

        openPrintPopover: (state) => {
            state.printPopoverActive = true;
        },

        closePrintPopover: (state) => {
            state.printPopoverActive = false;
        },

        setConfirmSavePopover: (state, action: PayloadAction<boolean>) => {
            state.confirmSavePopoverActive = action.payload;
        },

        addItems: (state, action: PayloadAction<AddAction>) => {
            addItems(state, action.payload);
        },

        removeItem: (state, action: PayloadAction<EditorItemKey>) => {
            if (state.document == null) {
                return;
            }

            state.focusedItem = null;
            const key = action.payload;
            const page = state.document.pages[key.page];
            page.splice(key.item, 1);
            if (isEmpty(page)) {
                state.document.pages.splice(key.page, 1);
            }
            updateItemSizesDistributionAndFocus(state);
        },

        moveItem: (state, action: PayloadAction<MoveAction>) => {
            if (state.document == null) {
                return;
            }

            const newIndex = action.payload.new;
            const oldIndex = action.payload.old;

            if (equals(newIndex, oldIndex)) {
                return;
            }

            if (!isExistingItemKey(state.document, oldIndex)) {
                return;
            }

            if (!isExistingItemKey(state.document, newIndex)) {
                return;
            }


            const pages = state.document.pages;
            const itemToMove = pages[oldIndex.page][oldIndex.item];

            pages[oldIndex.page].splice(oldIndex.item, 1);
            pages[newIndex.page].splice(newIndex.item, 0, itemToMove);

            updateItemSizesDistributionAndFocus(state);
        },

        moveItemByDir: (state, action: PayloadAction<MoveByOneAction>) => {
            if (state.document === null) {
                return;
            }

            const pages = state.document.pages;
            const offset = action.payload.dir,
                oldIndex = action.payload.itemKey;

            const newIndex = nextEditorItemKey(state.document, oldIndex, offset),
                itemToMove = pages[oldIndex.page][oldIndex.item];

            pages[oldIndex.page].splice(oldIndex.item, 1);
            pages[newIndex.page].splice(newIndex.item, 0, itemToMove);

            const focusedIndex = pages.slice(0, newIndex.page)
                .reduce((acc, page) => {
                    return acc + page.length
                }, newIndex.item);

            state.focusedItem = null;
            updateItemSizesDistributionAndFocus(state);

            const newPages = state.document.pages;
            let offsetLeft = focusedIndex, pageIndex;
            for (pageIndex = 0; pageIndex < newPages.length; pageIndex++) {
                const page = newPages[pageIndex];
                if (offsetLeft < page.length) {
                    break;
                }
                offsetLeft -= page.length;
            }

            state.focusedItem = {
                item: offsetLeft, page: pageIndex
            };
        },

        editItem: (state, action: PayloadAction<ReplaceAction>) => {
            if (state.document == null) {
                return;
            }
            if (!isExistingItemKey(state.document, action.payload.key)) {
                return;
            }

            const {page, item} = action.payload.key;
            state.document.pages[page][item] = action.payload.newItem;
            updateItemSizesDistributionAndFocus(state);
        },

        addWineCategory: (state, action: PayloadAction<AddWineCategoryAction>) => {
            if (state.document == null) {
                return;
            }

            const flatList = toKeyedEditorItemList(state.document.pages);
            const existingTitleIndex = flatList.findIndex(item => isTitleItem(item.menuItem) && item.menuItem.title === action.payload.category);
            if (action.payload.category && existingTitleIndex >= 0) {
                const firstNonWineAfterTitleIndex = flatList.findIndex((item, index) =>
                    index > existingTitleIndex && !isWineItem(item.menuItem));
                const lastWine = firstNonWineAfterTitleIndex >= 0 ? flatList[firstNonWineAfterTitleIndex - 1] : last(flatList);
                addItems(state, {
                    items: action.payload.wines,
                    after: lastWine?.itemKey
                });
            } else {
                let itemsToAdd: MenuItem[] = [];
                if (action.payload.category) {
                    itemsToAdd.push(createTitle(action.payload.category));
                }
                itemsToAdd = itemsToAdd.concat(action.payload.wines);
                
                addItems(state, {
                    items: itemsToAdd
                });
            }
        },

        setAllWinesDetailed: (state, action: PayloadAction<boolean>) => {
            if (state.document == null) {
                return;
            }

            state.document.pages = map(
                page => map(
                    item => isWineItem(item) ? assoc('detailed', action.payload, item) : item,
                    page
                ),
                state.document.pages
            );

            updateItemSizesDistributionAndFocus(state);
        }
    }
});

function isValidInsertionKey(document: MenuDocument, key: EditorItemKey) {
    return key.page in document.pages && key.item <= document.pages[key.page].length
}

function addItems(stateDraft: Draft<EditorState>, action: AddAction): void {
    if (stateDraft.document == null) {
        return;
    }

    if (stateDraft.document.pages.length === 0) {
        stateDraft.document.pages.push([]);
    }

    const addPosition = determineAddPosition(stateDraft.document, action);
    if (!isValidInsertionKey(stateDraft.document, addPosition)) {
        return;
    }

    const page = stateDraft.document.pages[addPosition.page]!;
    page.splice(addPosition.item, 0, ...action.items);
    stateDraft.focusedItem = addPosition;
    updateItemSizesDistributionAndFocus(stateDraft);
}

function determineAddPosition(document: MenuDocument, addRequest: AddAction): EditorItemKey {
    if (addRequest.after != null) {
        return evolve({item: add(1)}, addRequest.after);
    }

    const lastItem = getLastItemKey(document.pages);
    if (lastItem == null) {
        return {page: 0, item: 0};
    } else {
        return evolve({item: add(1)}, lastItem);
    }
}

function updateItemSizesDistributionAndFocus(state: Draft<EditorState>) {
    if (state.document == null) {
        return;
    }
    state.document.pages = updateDrinkSizes(state.document);
    state.document.pages = redistributeItemsToFitPages(state.document);
    state.focusedItem = refocusAfterItemRedistribution(state.document, state.focusedItem);
}

const unfocusActiveItem = createAsyncThunk("editorSlice/unfocus", async (_: void, {getState, dispatch}) => {
    const document = selectors.document(getState() as AppState);
    if (!document) return;
    createDocumentHandler(document).unfocusActiveItem(dispatch as AppDispatch);
})

function getEditorState(state: AppState) {
    return state.menuEditor;
}

const selectors = {
    winePickerActive: (state: AppState) => getEditorState(state).winePickerActive,
    wineScannerPickerActive: (state: AppState) => getEditorState(state).wineScannerPickerActive,
    aperitifPickerActive: (state: AppState) => getEditorState(state).aperitifPickerActive,
    formatOptionsActive: (state: AppState) => getEditorState(state).formatOptionsActive,
    coverPageEditorActive: (state: AppState) => getEditorState(state).coverPageEditorActive,
    document: (state: AppState) => getEditorState(state).document,
    displayFooter: (state: AppState) => getEditorState(state).document?.displayFooter,
    deletePopoverActive: (state: AppState) => getEditorState(state).deletePopoverActive,
    deletePopoverPage: (state: AppState) => getEditorState(state).deletePopoverPage,
    printPopoverActive: (state: AppState) => getEditorState(state).printPopoverActive,
    confirmSavePopoverActive: (state: AppState) => getEditorState(state).confirmSavePopoverActive,
    draggingItem: (state: AppState): EditorItem<MenuItem> | null => {
        const editorState = getEditorState(state);
        if (editorState.document == null || editorState.draggingItem == null) {
            return null;
        }

        return {
            itemKey: editorState.draggingItem,
            menuItem: editorState.document.pages[editorState.draggingItem.page][editorState.draggingItem.item]
        };
    },
    focusedItem: (state: AppState): EditorItem<MenuItem> | null => {
        const editorState = getEditorState(state);
        if (editorState.document == null || editorState.focusedItem == null) {
            return null;
        }

        return {
            itemKey: editorState.focusedItem,
            menuItem: editorState.document.pages[editorState.focusedItem.page][editorState.focusedItem.item]
        };
    }
}

const publicActions = {
    setDocument: actions.setDocument,
    resetDocumentState: actions.resetDocumentState,
    setCoverPage: actions.setCoverPage,
    setCoverPageVariant: actions.setCoverPageVariant,
    setCoverPageTitle: actions.setCoverPageTitle,
    setFormat: actions.setFormat,
    updateItemSizesDistributionAndFocus: actions.updateItemSizesDistributionAndFocus,
    setGlobalSuitableFood: actions.setGlobalSuitableFood,
    setIsDegu: actions.setIsDegu,
    setShowAppellation: actions.setShowAppellation,
    setShowYear: actions.setShowYear,
    setPrintMode: actions.setPrintMode,
    setSizes: actions.setSizes,
    setDisplayFooter: actions.setDisplayFooter,
    setWinePickerActive: actions.setWinePickerActive,
    setWineScannerPickerActive: actions.setWineScannerPickerActive,
    setAperitifPickerActive: actions.setAperitifPickerActive,
    setFormatOptionsActive: actions.setFormatOptionsActive,
    setCoverPageEditorActive: actions.setCoverPageEditorActive,
    openDeletePopover: actions.openDeletePopover,
    closeDeletePopover: actions.closeDeletePopover,
    deletePage: actions.deletePage,
    openPrintPopover: actions.openPrintPopover,
    closePrintPopover: actions.closePrintPopover,
    setConfirmSavePopover: actions.setConfirmSavePopover,
    setDraggingItem: actions.setDraggingItem,
    setFocusedItem: actions.setFocusedItem,
    addItems: actions.addItems,
    removeItem: actions.removeItem,
    moveItem: actions.moveItem,
    moveItemByDir: actions.moveItemByDir,
    editItem: actions.editItem,
    addWineCategory: actions.addWineCategory,
    setAllWinesDetailed: actions.setAllWinesDetailed,
    unfocusActiveItem: unfocusActiveItem
}

export {publicActions as menuEditorActions};
export {selectors as menuEditorSelectors};
export {reducer as menuEditorReducer};
